import React, { useEffect, useState } from "react";
import api from "./api/api";

import {
Alert,
Autocomplete,
Box,
Button,
CircularProgress,
Dialog,
DialogContent,
IconButton,
InputAdornment,
Paper,
TextField,
Typography,
} from "@mui/material";

import {
LockReset,
Visibility,
VisibilityOff,
PersonSearch,
Security,
} from "@mui/icons-material";

const ORANGE = "#ff6b00";
const BLACK = "#111111";
const CHAMELEON = "#b7e000";
const LIGHT_BG = "#f7f7f7";

export default function AdminResetPassword({
open,
onClose,
}) {
const [users, setUsers] = useState([]);
const [selectedUser, setSelectedUser] = useState(null);
const [newPassword, setNewPassword] = useState("");


const [message, setMessage] = useState("");
const [error, setError] = useState("");

const [loadingUsers, setLoadingUsers] = useState(false);
const [resetting, setResetting] = useState(false);
const [showPassword, setShowPassword] = useState(false);

useEffect(() => {
    if (!open) return;

    const fetchUsers = async () => {
        setLoadingUsers(true);
        setError("");

        try {
            const token = localStorage.getItem("access");

            const res = await api.get(
                "/api/users/users-users/",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setUsers(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching users:", err);

            setError(
                err.response?.data?.detail ||
                "Unable to load users."
            );
        } finally {
            setLoadingUsers(false);
        }
    };

    fetchUsers();
}, [open]);

const handleReset = async () => {
    setMessage("");
    setError("");

    if (!selectedUser) {
        setError("Please select a user.");
        return;
    }

    if (!newPassword.trim()) {
        setError("Please enter a new password.");
        return;
    }

    if (newPassword.length < 6) {
        setError("Password must be at least 6 characters long.");
        return;
    }

    try {
        setResetting(true);

        const token = localStorage.getItem("access");

        const res = await api.post(
            "/api/users/admin-reset-password/",
            {
                user_id: selectedUser.id,
                new_password: newPassword,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        setMessage(
            res.data?.message ||
            "Password reset successfully."
        );

        setSelectedUser(null);
        setNewPassword("");
    } catch (err) {
        console.error("Error resetting password:", err);

        setError(
            err.response?.data?.error ||
            err.response?.data?.detail ||
            "Error resetting password."
        );
    } finally {
        setResetting(false);
    }
};

const handleClose = () => {
    setSelectedUser(null);
    setNewPassword("");
    setMessage("");
    setError("");

    if (onClose) {
        onClose();
    }
};

return (
    <Dialog
        open={open}
        onClose={() => {}}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
        PaperProps={{
            sx: {
                borderRadius: "24px",
                overflow: "hidden",
                background: "#ffffff",
                boxShadow: "0 25px 80px rgba(0,0,0,0.35)",
            },
        }}
    >
        {/* HEADER */}
        <Box
            sx={{
                position: "relative",
                background: `
                    linear-gradient(
                        135deg,
                        ${BLACK} 0%,
                        #252525 65%,
                        ${ORANGE} 100%
                    )
                `,
                px: { xs: 3, sm: 4 },
                py: 3.5,
                color: "#fff",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: -45,
                    right: -35,
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    background: CHAMELEON,
                    opacity: 0.85,
                }}
            />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        width: 58,
                        height: 58,
                        borderRadius: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: ORANGE,
                        color: "#fff",
                        boxShadow: "0 8px 20px rgba(255,107,0,0.35)",
                    }}
                >
                    <LockReset sx={{ fontSize: 32 }} />
                </Box>

                <Box>
                    <Typography
                        sx={{
                            fontSize: {
                                xs: 20,
                                sm: 24,
                            },
                            fontWeight: 800,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        Reset User Password
                    </Typography>

                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: 13,
                            color: "rgba(255,255,255,0.75)",
                        }}
                    >
                        Securely update a user's login credentials
                    </Typography>
                </Box>
            </Box>
        </Box>

        {/* CONTENT */}
        <DialogContent
            sx={{
                p: {
                    xs: 2.5,
                    sm: 4,
                },
                background: LIGHT_BG,
            }}
        >
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    borderRadius: "14px",
                    background: "#fff",
                    borderLeft: `5px solid ${CHAMELEON}`,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                }}
            >
                <Security
                    sx={{
                        color: ORANGE,
                        fontSize: 25,
                    }}
                />

                <Typography
                    sx={{
                        fontSize: 13,
                        color: "#555",
                        lineHeight: 1.6,
                    }}
                >
                    Select the account that requires a password reset
                    and create a new secure password.
                </Typography>
            </Box>

            {/* USER SELECTION */}
            <Autocomplete
                options={users}
                value={selectedUser}
                loading={loadingUsers}
                onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                    setError("");
                    setMessage("");
                }}
                getOptionLabel={(option) =>
                    `${option.username || "Unknown User"} ${
                        option.email
                            ? `(${option.email})`
                            : ""
                    }`
                }
                isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                }
                noOptionsText="No users found"
                PaperComponent={({ children }) => (
                    <Paper
                        sx={{
                            maxHeight: 280,
                            overflowY: "auto",
                            borderRadius: "12px",
                            mt: 1,
                            boxShadow:
                                "0 10px 30px rgba(0,0,0,0.15)",
                        }}
                    >
                        {children}
                    </Paper>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select User"
                        placeholder="Search username or email..."
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        <PersonSearch
                                            sx={{
                                                color: ORANGE,
                                            }}
                                        />
                                    </InputAdornment>

                                    {params.InputProps.startAdornment}
                                </>
                            ),
                            endAdornment: (
                                <>
                                    {loadingUsers ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{
                                                color: ORANGE,
                                            }}
                                        />
                                    ) : null}

                                    {
                                        params.InputProps
                                            .endAdornment
                                    }
                                </>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "14px",
                                background: "#fff",
                                "&:hover fieldset": {
                                    borderColor: ORANGE,
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: ORANGE,
                                    borderWidth: 2,
                                },
                            },
                            "& label.Mui-focused": {
                                color: ORANGE,
                            },
                        }}
                    />
                )}
            />

            {/* PASSWORD */}
            <TextField
                label="New Password"
                placeholder="Enter a secure password"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={newPassword}
                onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError("");
                    setMessage("");
                }}
                sx={{
                    mt: 2.5,
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        background: "#fff",
                        "&:hover fieldset": {
                            borderColor: ORANGE,
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: ORANGE,
                            borderWidth: 2,
                        },
                    },
                    "& label.Mui-focused": {
                        color: ORANGE,
                    },
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() =>
                                    setShowPassword(
                                        !showPassword
                                    )
                                }
                                edge="end"
                            >
                                {showPassword ? (
                                    <VisibilityOff />
                                ) : (
                                    <Visibility />
                                )}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            {/* ALERTS */}
            {message && (
                <Alert
                    severity="success"
                    sx={{
                        mt: 2.5,
                        borderRadius: "12px",
                        fontWeight: 600,
                    }}
                >
                    {message}
                </Alert>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mt: 2.5,
                        borderRadius: "12px",
                        fontWeight: 600,
                    }}
                >
                    {error}
                </Alert>
            )}

            {/* ACTIONS */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                    mt: 4,
                    flexDirection: {
                        xs: "column-reverse",
                        sm: "row",
                    },
                }}
            >
                {/* ONLY CLOSE BUTTON */}
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                        minWidth: 120,
                        height: 46,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: BLACK,
                        borderColor: "#cccccc",
                        "&:hover": {
                            borderColor: BLACK,
                            background: "#f1f1f1",
                        },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleReset}
                    disabled={resetting}
                    startIcon={
                        resetting ? (
                            <CircularProgress
                                size={18}
                                sx={{
                                    color: "#fff",
                                }}
                            />
                        ) : (
                            <LockReset />
                        )
                    }
                    sx={{
                        minWidth: 190,
                        height: 46,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 800,
                        background: ORANGE,
                        boxShadow:
                            "0 8px 18px rgba(255,107,0,0.25)",
                        "&:hover": {
                            background: "#d95700",
                            boxShadow:
                                "0 10px 22px rgba(255,107,0,0.35)",
                        },
                    }}
                >
                    {resetting
                        ? "Resetting..."
                        : "Reset Password"}
                </Button>
            </Box>
        </DialogContent>
    </Dialog>
);


}


